import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "@/components/motion";

const APP_URL = "https://app.uzakyonetim.com.tr";

const SECTIONS = [
  { label: "Özellikler", id: "ozellikler" },
  { label: "Nasıl Çalışır", id: "nasil-calisir" },
  { label: "İletişim", id: "iletisim" },
];

export const Logo = ({ dark = false, size = "md" }) => (
  <img
    src={dark ? "/assets/logo-lockup-dark.png" : "/assets/logo-lockup.png"}
    alt="UzakYönetim RMM — uzakyonetim.com.tr, Remote Management Platform"
    className={`w-auto ${size === "lg" ? "h-16 md:h-20" : "h-9 md:h-10"} ${
      dark ? "drop-shadow-[0_2px_14px_rgba(120,160,220,0.25)]" : ""
    }`}
  />
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const sectionHref = (id) => (pathname === "/" ? `#${id}` : `/#${id}`);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
      className="fixed inset-x-0 top-0 z-50 border-b border-black/5 bg-white/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:h-20 md:px-12">
        <a href={sectionHref("top")} data-testid="navbar-logo" aria-label="UzakYönetim ana sayfa">
          <Logo />
        </a>

        <nav className="hidden items-center gap-10 md:flex" aria-label="Ana menü">
          {SECTIONS.map((l) => (
            <a
              key={l.id}
              href={sectionHref(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="link-underline text-sm font-medium text-[#525252] transition-colors duration-300 hover:text-[#0A0A0A]"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/fiyatlar"
            data-testid="nav-link-fiyatlar"
            className={`link-underline text-sm font-medium transition-colors duration-300 ${
              pathname === "/fiyatlar" ? "text-[#003366]" : "text-[#525252] hover:text-[#0A0A0A]"
            }`}
          >
            Fiyatlar
          </Link>
        </nav>

        <div className="hidden md:block">
          <motion.a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-login-button"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-2 rounded-full bg-[#003366] px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#336699]"
          >
            Giriş Yap
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
          </motion.a>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="text-[#0A0A0A] md:hidden"
          aria-label="Menüyü aç/kapat"
        >
          {open ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="navbar-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden border-t border-black/5 bg-white/90 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {SECTIONS.map((l) => (
                <a
                  key={l.id}
                  href={sectionHref(l.id)}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-3 text-base font-medium text-[#525252] hover:bg-black/5"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/fiyatlar"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-[#525252] hover:bg-black/5"
              >
                Fiyatlar
              </Link>
              <a
                href={APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="navbar-mobile-login-button"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#003366] px-6 py-3 text-sm font-semibold text-white"
              >
                Giriş Yap
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
